import { Grid, TextField, Typography, Button, CircularProgress } from "@material-ui/core";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getLanguage } from "@utils/changeLanguage";
import PhoneInput from "react-phone-input-2";
import "./phoneInputFiles/phoneInput.scss";
import gr from "./phoneInputFiles/lang/gr.json";
import { PhoneNumberUtil } from "google-libphonenumber";
import { IValidateOtpResponse, useChangeContactNumber } from "./hooks/useChangeContactNumber";

interface IParams {
    handleCancelClick(): void;
    onFinishUpdate(): void;
}

const phoneUtil = PhoneNumberUtil.getInstance();

const UpdateContactNumber = (props: IParams) => {
    const { handleCancelClick, onFinishUpdate } = props;
    const [isMobileNumberValid, setIsMobileNumberValid] = useState<boolean>(true);
    const [isOtpCodeValid, setIsOtpCodeValid] = useState<IValidateOtpResponse>({ success: true, validOtp: true });
    const [showOtp, setShowOtp] = useState<boolean>(false);
    const [OTP, setOTP] = useState<string>("");
    const { formMethods, handleSendOtp, sendOtpState, handleValidateOtp, validateOtpState } = useChangeContactNumber(
        setIsMobileNumberValid,
        setIsOtpCodeValid,
        setShowOtp,
        onFinishUpdate
    );
    const { t } = useTranslation(["profile", "common", "validation"]);
    const { errors, control, clearErrors, getValues } = formMethods;

    const validatePhoneNumber = (number: string) => {
        let error: string | undefined = undefined;

        try {
            const num = "+".concat(number);
            if (!phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(num))) {
                error = t("profile:phoneNumberError");
            }
        } catch (e: any) {
            error = e.message;
        }

        error?.length ? setIsMobileNumberValid(false) : setIsMobileNumberValid(true);
        return error;
    };

    const resetOtpStatus = () => {
        setOTP("");
        setIsOtpCodeValid({ validOtp: true, success: true });
    };

    const onMobileNumberChange = () => {
        if (showOtp) {
            setShowOtp(false);
            resetOtpStatus();
        }

        if (errors.mobileNumber) clearErrors("mobileNumber");
    };

    return (
        <Grid container item>
            <form className="mydetail-email-form">
                <Grid item container direction={"row"} spacing={1}>
                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            control={control}
                            name="mobileNumber"
                            render={(controllerProps) => (
                                <PhoneInput
                                    disabled={sendOtpState.isLoading || validateOtpState.isLoading}
                                    country={"cy"}
                                    preferredCountries={["cy"]}
                                    countryCodeEditable={false}
                                    localization={getLanguage() === "el" ? gr : undefined}
                                    enableSearch={true}
                                    value={controllerProps.value}
                                    specialLabel={`${t("profile:contactNumber")} *`}
                                    searchPlaceholder={t("profile:searchCountry")}
                                    searchNotFound={t("profile:noCountryFound")}
                                    onChange={(phone: any) => {
                                        onMobileNumberChange();
                                        controllerProps.onChange(phone);
                                    }}
                                    isValid={() => {
                                        return isMobileNumberValid;
                                    }}
                                />
                            )}
                            rules={{
                                required: t("validation:required"),
                                validate: (val: string) => validatePhoneNumber(val),
                            }}
                        />
                        {formMethods.errors.mobileNumber && (
                            <Typography color="error" variant="body1">
                                {formMethods.errors.mobileNumber.message}
                            </Typography>
                        )}
                    </Grid>
                    {!showOtp && (
                        <Grid item style={{ marginTop: "10px" }}>
                            <Button
                                disabled={sendOtpState.isLoading}
                                color="secondary"
                                type="button"
                                variant="contained"
                                onClick={async () => {
                                    if (await formMethods.trigger("mobileNumber")) {
                                        handleSendOtp(getValues("mobileNumber"));
                                    }
                                }}
                            >
                                {sendOtpState.isLoading ? <CircularProgress size={20} /> : t("profile:verifyNumber")}
                            </Button>
                        </Grid>
                    )}
                </Grid>
                {!showOtp && sendOtpState.error && (
                    <Grid item xs={12}>
                        <Typography color="error" variant="body1">
                            {sendOtpState.error.errorMessage}
                        </Typography>
                    </Grid>
                )}
                {showOtp && (
                    <Grid item container direction={"row"} spacing={1}>
                        <Grid item md={4} sm={6} xs={12}>
                            <TextField
                                disabled={validateOtpState.isLoading}
                                fullWidth
                                label={`${t("profile:enterOTP")} *`}
                                margin="dense"
                                type="text"
                                value={OTP}
                                name="OTP"
                                onChange={(event) => {
                                    setOTP(event.target.value);
                                }}
                            />
                            <Typography color="textSecondary" gutterBottom id="mobileOtpHelper" variant="body2">
                                {t("profile:mobileOtpHelper")}
                            </Typography>
                            {!isOtpCodeValid.success && isOtpCodeValid.validOtp && (
                                <Typography color="error" variant="body2">
                                    {t("profile:sendOtpFailed")}
                                </Typography>
                            )}
                            {!isOtpCodeValid.validOtp && (
                                <Typography color="error" variant="body2">
                                    {t("profile:invalidOTP")}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item style={{ marginTop: "10px" }}>
                            <Button
                                disabled={validateOtpState.isLoading || !OTP}
                                color="secondary"
                                type="button"
                                variant="contained"
                                onClick={async () => {
                                    handleValidateOtp(getValues("mobileNumber"), OTP);
                                }}
                            >
                                {validateOtpState.isLoading ? <CircularProgress size={20} /> : t("profile:verifyOTP")}
                            </Button>
                        </Grid>
                        {(!isOtpCodeValid.success || !isOtpCodeValid.validOtp) && (
                            <Grid item style={{ marginTop: "10px" }}>
                                <Button
                                    disabled={sendOtpState.isLoading}
                                    color="secondary"
                                    type="button"
                                    variant="contained"
                                    onClick={async () => {
                                        resetOtpStatus();
                                        handleSendOtp(getValues("mobileNumber"));
                                    }}
                                >
                                    {sendOtpState.isLoading ? <CircularProgress size={20} /> : t("profile:resendOTP")}
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                )}
                {showOtp && validateOtpState.error && (
                    <Grid item xs={12}>
                        <Typography color="error" variant="body1">
                            {validateOtpState.error.errorMessage}
                        </Typography>
                    </Grid>
                )}
                {showOtp && sendOtpState.error && (
                    <Grid item xs={12}>
                        <Typography color="error" variant="body1">
                            {sendOtpState.error.errorMessage}
                        </Typography>
                    </Grid>
                )}
                <Grid container item justify="center" alignItems="center" style={{ margin: "10px 0 10px 0" }}>
                    <Grid
                        item
                        container
                        spacing={2}
                        direction="row"
                        sm={8}
                        xs={12}
                        justify="center"
                        alignItems="center"
                    >
                        <Grid item xs={4}>
                            <Button
                                disabled={sendOtpState.isLoading || validateOtpState.isLoading}
                                color="secondary"
                                fullWidth
                                variant="outlined"
                                onClick={handleCancelClick}
                            >
                                {t("common:cancel")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
};

export default UpdateContactNumber;
